import { useSponsors } from '../../hooks/useSponsors';
import Reveal from './Reveal';
import { getImageUrl } from '../../utils/helpers';

const Sponsors: React.FC = () => {
  const { data: partners, isLoading } = useSponsors();

  if (isLoading || !partners) return null;

  const goldPartners = partners.filter(p => p.level === 'gold');
  const silverPartners = partners.filter(p => p.level === 'silver');
  const bronzePartners = partners.filter(p => !p.level || p.level === 'bronze');

  return (
    <section className="py-32 bg-white relative overflow-hidden" id="parceiros">
      {/* Estilos para o Marquee Infinito e animações customizadas */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: max-content;
          animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.02); }
        }
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
      `}} />

      {/* Decorative background elements - Very subtle */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-brand-green/5 rounded-full blur-[120px] -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-brand-yellow/5 rounded-full blur-[120px] translate-y-1/2"></div>

      <div className="container mx-auto px-4 relative z-10">
        <Reveal>
          <div className="text-center max-w-4xl mx-auto mb-24">
            <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-8 tracking-tight leading-[1.1]">
              Quem viabiliza nossa <span className="text-brand-green underline decoration-brand-yellow/30 decoration-8 underline-offset-8">Missão</span>
            </h2>
            <p className="text-slate-500 font-bold text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed">
              Empresas parceiras que investem na proteção integral de crianças e adolescentes.
            </p>
          </div>
        </Reveal>

        <div className="flex flex-col gap-32 items-center">
          
          {/* DIAMANTE / OURO - CIRCULAR FLOATING CARDS */}
          {goldPartners.length > 0 && (
            <div className="w-full">
              <Reveal>
                <div className="flex flex-wrap justify-center gap-12 md:gap-20">
                  {goldPartners.map((partner, index) => (
                    <div key={partner.id} className={`flex flex-col items-center animate-float`} style={{ animationDelay: `${index * 0.5}s` }}>
                      <a
                        href={partner.url}
                        target="_blank"
                        rel="noreferrer"
                        className="group relative w-48 h-48 md:w-64 md:h-64 flex items-center justify-center bg-white rounded-full p-10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border-4 border-slate-50 hover:border-brand-yellow hover:shadow-[0_30px_70px_rgba(254,193,14,0.25)] transition-all duration-700 active:scale-95 overflow-hidden"
                      >
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/40 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        
                        <img
                          loading="lazy"
                          src={getImageUrl(partner.image)}
                          alt={partner.name}
                          className="w-full h-full object-contain relative z-10 drop-shadow-sm group-hover:scale-110 transition-transform duration-700"
                        />
                        
                        {/* Status tag */}
                        <div className="absolute inset-0 border-[12px] border-brand-yellow/5 rounded-full"></div>
                      </a>
                      <span className="mt-6 text-brand-yellow font-black uppercase tracking-[0.4em] text-xs">Master Partner</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          )}

          {/* SILVER TIER - CLEAN CIRCLES */}
          {silverPartners.length > 0 && (
            <div className="w-full max-w-6xl">
              <Reveal delay={0.2}>
                <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                  {silverPartners.map((partner) => (
                    <a
                      key={partner.id}
                      href={partner.url}
                      target="_blank"
                      rel="noreferrer"
                      className="group w-32 h-32 md:w-40 md:h-40 flex items-center justify-center p-6 bg-slate-50 rounded-full border-2 border-transparent hover:border-brand-green/30 hover:bg-white hover:shadow-xl transition-all duration-300"
                    >
                      <img
                        loading="lazy"
                        src={getImageUrl(partner.image)}
                        alt={partner.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </div>

      {/* BRONZE TIER - INFINITE MARQUEE (NIVEL 2026) */}
      {bronzePartners.length > 0 && (
        <div className="mt-32 w-full bg-slate-50 py-12 border-y border-slate-100 overflow-hidden group/marquee">
          <div className="container mx-auto px-4 mb-8">
            <h4 className="text-center text-slate-400 font-black uppercase tracking-[0.3em] text-[10px]">Rede de Apoio Regional</h4>
          </div>
          
          <div className="relative">
            {/* Margins for the marquee to fade out */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 to-transparent z-10"></div>

            <div className="animate-marquee gap-12 px-12">
              {/* Double the list to create the infinite effect */}
              {[...bronzePartners, ...bronzePartners, ...bronzePartners].map((partner, idx) => (
                <a
                  key={`${partner.id}-${idx}`}
                  href={partner.url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-20 h-20 md:w-28 md:h-28 shrink-0 flex items-center justify-center p-4 bg-white rounded-full shadow-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-300 border border-slate-200/50"
                >
                  <img
                    loading="lazy"
                    src={getImageUrl(partner.image)}
                    alt={partner.name}
                    className="w-full h-full object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FINAL CALL TO ACTION */}
      <div className="container mx-auto px-4 mt-24">
        <Reveal>
          <div className="relative max-w-5xl mx-auto rounded-[4rem] overflow-hidden group/cta">
            {/* Background design */}
            <div className="absolute inset-0 bg-brand-darkGreen"></div>
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-brand-green rounded-full blur-[80px] opacity-20 group-hover/cta:opacity-40 transition-opacity"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-brand-yellow rounded-full blur-[80px] opacity-10 group-hover/cta:opacity-30 transition-opacity"></div>

            <div className="relative z-10 px-8 py-16 md:px-20 md:py-24 flex flex-col md:flex-row items-center justify-between gap-12">
              <div className="text-center md:text-left max-w-xl">
                <h3 className="text-white text-3xl md:text-5xl font-black mb-6 leading-tight">
                  Sua marca no <br />
                  <span className="text-brand-yellow">Coração do Litoral</span>
                </h3>
                <p className="text-white/60 text-lg md:text-xl font-medium mb-0">
                  Faça parte do grupo seletivo de empresas que transformam a realidade da nossa região.
                </p>
              </div>

              <div className="flex flex-col items-center gap-4">
                <a href="https://wa.me/5551982755065"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-4 bg-brand-yellow text-brand-darkGreen px-10 py-6 rounded-full font-black text-xl hover:bg-white hover:scale-105 hover:shadow-[0_20px_50px_rgba(254,193,14,0.3)] transition-all duration-500 shadow-2xl active:scale-95 group/btn">
                  QUERO SER PARCEIRO
                  <i className="fas fa-arrow-right group-hover/btn:translate-x-2 transition-transform"></i>
                </a>
                <span className="text-white/30 font-black uppercase tracking-[0.3em] text-[10px]">Atendimento imediato via WhatsApp</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Sponsors;